import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * A manual Abstimmung of the LAK, entered by the PSB next to the Übertrag. It runs in parallel to the contract
 * intervalls and adds to or takes from the balance, for what the contracts cannot express - someone working a few
 * hours during Elternzeit while drawing an hour per week, or hours which are paid out.
 *
 * Two kinds, told apart by `end`:
 * - per week: `start` to `end` with `hoursPerWeek`, credited over the whole weeks in between like a contract
 * - one-time: only `start` with `hours`, booked on that day
 */
@Entity()
export class LAKAdjustment extends AdornisEntity {
  static override _class = 'LAKAdjustment';

  static override resolveID(entity: { _id: Maybe<string> }): string {
    return `${entity._id}`;
  }

  constructor(doc: PartialEntityData<LAKAdjustment>) {
    super(doc);
  }

  @Field(() => ID)
  _id: Maybe<string>;

  /**
   * Why the Abstimmung exists. The overview shows it next to the hours, so it has to explain them on its own.
   */
  @Field(() => String)
  name: Maybe<string>;

  @Field(() => Date)
  start: Maybe<Date>;

  /**
   * The last day of a per-week Abstimmung. Unset for a one-time booking.
   */
  @Field(() => Date)
  end: Maybe<Date>;

  /**
   * Added per whole week of a per-week Abstimmung, negative to take hours out.
   */
  @Field(() => Float)
  hoursPerWeek: Maybe<number>;

  /**
   * Booked once on `start` by a one-time Abstimmung, negative to take hours out.
   */
  @Field(() => Float)
  hours: Maybe<number>;
}
